import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, RealtimeProvider, useAuth } from "@/context/AppContext";
import { Spinner } from "@/components/common";
import Layout from "@/components/Layout";
import { LoginPage, ForgotPage, ResetPage, ForceChangePage } from "@/pages/Auth";
import { SwitchedOverlay } from "@/components/SessionGuard";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
// Code split by section: Dashboard + Messages load first, the other sections are fetched in the background right after
// (so opening them is still instant) instead of all in the first download.
const PAGES = {
  Creators: () => import("@/pages/Creators"), CreatorDetail: () => import("@/pages/CreatorDetail"), TelegramAccounts: () => import("@/pages/TelegramAccounts"),
  Fans: () => import("@/pages/Fans"), Analytics: () => import("@/pages/AnalyticsV2"), Reports: () => import("@/pages/Reports"), BotHistory: () => import("@/pages/BotHistory"), FolderBot: () => import("@/pages/FolderBot"), Turni: () => import("@/pages/Turni"),
  Calendar: () => import("@/pages/Calendar"), ChatterProfile: () => import("@/pages/ChatterProfile"), Leaderboard: () => import("@/pages/Leaderboard"), ChatterCreators: () => import("@/pages/ChatterCreators"),
  Vault: () => import("@/pages/Vault"), Users: () => import("@/pages/Users"),
  Settings: () => import("@/pages/Settings"), Sales: () => import("@/pages/Sales"), CreatorArea: () => import("@/pages/CreatorArea"),
  MarketingDashboard: () => import("@/pages/Marketing"), MarketingVault: () => import("@/pages/MarketingVault"),
};
const L = Object.fromEntries(Object.entries(PAGES).map(([k, f]) => [k, lazy(f)]));
const { ChatterCreators, Creators, CreatorDetail, TelegramAccounts, Fans, Analytics, Reports, BotHistory, FolderBot, Turni, Calendar, ChatterProfile, Leaderboard, Vault, Users, Settings, Sales, CreatorArea, MarketingDashboard, MarketingVault } = L;
const MarketingLayout = lazy(() => import("@/pages/Marketing").then((m) => ({ default: m.MarketingLayout })));
if (typeof window !== "undefined") {
  const pre = () => Object.values(PAGES).forEach((f) => f().catch(() => {}));
  window.addEventListener("load", () => (window.requestIdleCallback ? window.requestIdleCallback(pre, { timeout: 3000 }) : setTimeout(pre, 1500)));
}

// Old client links /u/...: generic 404, nothing about us (no logo, no name, no title).
function Blank404() {
  useEffect(() => { document.title = "404"; }, []);
  return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", color: "#555", fontFamily: "sans-serif" }} data-testid="blank-404">404 Not Found</div>;
}
import { isStaff, isAdmin } from "@/lib/format";

function Guard({ children, staff, admin, creatorOk, marketingOk }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen"><Spinner className="h-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForceChangePage />;
  if (user.role === "creator" && !creatorOk) return <Navigate to="/creator" replace />;  // creators: only their own screen
  if (user.role === "marketing" && !marketingOk) return <Navigate to="/marketing" replace />;  // marketing: only its dashboard + vault
  if (marketingOk && user.role !== "marketing") return <Navigate to="/" replace />;
  if (creatorOk && !["creator", "owner", "admin", "manager", "viewer"].includes(user.role)) return <Navigate to="/" replace />;
  if ((staff && !isStaff(user)) || (admin && !isAdmin(user))) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen"><Spinner className="h-full" /></div>;
  return user ? <Navigate to="/" replace /> : children;
}

export default function App() {
  return (
    <div className="dark">
      <AuthProvider>
        <RealtimeProvider>
          <BrowserRouter>
            <Suspense fallback={<div className="h-screen"><Spinner className="h-full" /></div>}>
            <Routes>
              <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><ForgotPage /></PublicOnly>} />
              <Route path="/reset-password" element={<ResetPage />} />
              <Route path="/u/*" element={<Blank404 />} />
              <Route path="/creator" element={<Guard creatorOk><Suspense fallback={<div className="h-screen"><Spinner className="h-full" /></div>}><CreatorArea /></Suspense></Guard>} />
              <Route path="/marketing" element={<Guard marketingOk><MarketingLayout /></Guard>}>
                <Route index element={<MarketingDashboard />} />
                <Route path="vault" element={<MarketingVault />} />
              </Route>
              <Route element={<Guard><Layout /></Guard>}>
                <Route index element={<Dashboard />} />
                <Route path="messages" element={<Messages />} />
                <Route path="creators" element={<Guard staff><Creators /></Guard>} />
                <Route path="creators/:id" element={<Guard staff><CreatorDetail /></Guard>} />
                <Route path="supervision" element={<Navigate to="/bot-history" replace />} />
                <Route path="supervisione" element={<Navigate to="/bot-history" replace />} />
                <Route path="telegram" element={<TelegramAccounts />} />
                <Route path="fans" element={<Fans />} />
                <Route path="shifts" element={<Turni />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="leaderboard" element={<Guard staff><Leaderboard /></Guard>} />
                <Route path="sales" element={<Sales />} />
                <Route path="chatters/:id" element={<Guard staff><ChatterProfile /></Guard>} />
                <Route path="vault" element={<Vault />} />
                <Route path="settings" element={<Settings />} />
                <Route path="my-stats" element={<ChatterProfile mine />} />
                <Route path="my-creators" element={<ChatterCreators />} />
                <Route path="analytics" element={<Guard staff><Analytics /></Guard>} />
                <Route path="reports" element={<Guard staff><Reports /></Guard>} />
                <Route path="bot-history" element={<Guard staff><BotHistory /></Guard>} />
                <Route path="folder-bot" element={<Guard staff><FolderBot /></Guard>} />
                <Route path="chatters" element={<Guard staff><Users /></Guard>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          <SwitchedOverlay />
          <Toaster theme="dark" position="bottom-right" richColors closeButton />
        </RealtimeProvider>
      </AuthProvider>
    </div>
  );
}
